import {
  AppFeature,
  AvailabilityStatus,
  HealthProviderFeatureInput,
  HealthProviderFeatureItemDataFragment,
} from '../../../data/types/api/graphql';

/**
 * DELETE_CHAT is practice-wide: a row with it in any availability status turns it on
 * for every status.
 */
export function isDeleteChatEnabled(
  items: HealthProviderFeatureItemDataFragment[] | null | undefined
): boolean {
  return (items ?? []).some(item =>
    item.appFeatures.includes(AppFeature.DeleteChat)
  );
}

/**
 * Builds the mutations for saving one status's features plus the practice-wide
 * DELETE_CHAT switch. Turning it on writes it on the edited status (unless another
 * status already carries it); turning it off strips it from every status, leaving
 * their other features unchanged. The edited status always comes first: callers
 * send the updates in order and stop at the first refusal.
 */
export function buildHealthProviderFeatureUpdates({
  items,
  availabilityStatus,
  appFeatures,
  deleteChat,
}: {
  items: HealthProviderFeatureItemDataFragment[] | null | undefined;
  availabilityStatus: AvailabilityStatus | null | undefined;
  appFeatures: AppFeature[];
  deleteChat: boolean;
}): HealthProviderFeatureInput[] {
  const ownFeatures: AppFeature[] = appFeatures.filter(f => f !== AppFeature.DeleteChat);
  const ownItem = (items ?? []).find(
    item => item.availabilityStatus === availabilityStatus
  );
  const ownHasDeleteChat =
    ownItem?.appFeatures.includes(AppFeature.DeleteChat) ?? false;

  if (deleteChat) {
    if (ownHasDeleteChat || !isDeleteChatEnabled(items)) {
      ownFeatures.push(AppFeature.DeleteChat);
    }

    return [{ availabilityStatus, appFeatures: ownFeatures }];
  }

  const otherUpdates = (items ?? [])
    .filter(
      item =>
        item.availabilityStatus !== availabilityStatus &&
        item.appFeatures.includes(AppFeature.DeleteChat)
    )
    .map(item => ({
      availabilityStatus: item.availabilityStatus,
      appFeatures: item.appFeatures.filter(f => f !== AppFeature.DeleteChat),
    }));

  return [{ availabilityStatus, appFeatures: ownFeatures }, ...otherUpdates];
}
